/**
 * useAuth() — Auth-Gate für die App-Shell (KONZEPT §10).
 *
 * Lädt `/api/auth/me` beim Mount. Solange der Request läuft → `loading`.
 * 401 (keine Session) → `loggedIn: false` (App rendert den Login-Screen).
 * Bei Erfolg liefert der Hook den eingeloggten Owner inkl. Rolle sowie die für
 * ihn sichtbaren Konten. `logout()` beendet die Session und setzt den Zustand
 * zurück.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AuthUserDto, OwnerRole } from '@ads/contracts';
import { getMe, logout as apiLogout, setReauthHandler, ApiError, type MeView } from './api';
import type { AccountFixture } from './types';

export interface AuthState {
  /** Eingeloggter Owner (oder null solange/falls nicht eingeloggt). */
  user: AuthUserDto | null;
  /** Rolle des eingeloggten Owners (oder null). */
  role: OwnerRole | null;
  /** Für den Owner sichtbare Konten (eigene + geteilte), bereits view-gemappt. */
  accounts: AccountFixture[];
  /** true = `/auth/me` läuft noch (Initial-Load). */
  loading: boolean;
  /** true = gültige Session vorhanden. */
  loggedIn: boolean;
  /** Session beenden und Zustand zurücksetzen. */
  logout: () => Promise<void>;
  /** Session erneut laden (z.B. nach Rückkehr vom OAuth-Redirect). */
  reload: () => Promise<void>;
}

export function useAuth(): AuthState {
  const [me, setMe] = useState<MeView | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setMe(await getMe());
    } catch (err) {
      // 401 = keine Session → regulärer Logout-Zustand; alles andere ebenso
      // als „nicht eingeloggt" behandelt, damit der Login-Screen greift.
      if (!(err instanceof ApiError) || err.status !== 401) {
        // eslint-disable-next-line no-console
        console.error('Auth-Check fehlgeschlagen:', err);
      }
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Reauth-Handler: wenn Google invalid_grant zurückgibt (GOOGLE_REAUTH_REQUIRED),
  // setzt apiFetch diesen Handler ab → App loggt den Operator lokal aus, damit
  // er sich neu einloggt und Google Ads neu autorisiert. Best-effort Server-Logout
  // wird versucht (Token ist sowieso abgelaufen, Fehler werden ignoriert).
  useEffect(() => {
    setReauthHandler(() => {
      void apiLogout().catch(() => { /* abgelaufener Token → ignorieren */ });
      setMe(null);
    });
    return () => {
      setReauthHandler(null);
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setMe(null);
    }
  }, []);

  return {
    user: me?.user ?? null,
    role: me?.user.role ?? null,
    accounts: me?.accounts ?? [],
    loading,
    loggedIn: me !== null,
    logout,
    reload,
  };
}
